/**
 * Validação algorítmica de CPF e CNPJ (dígitos verificadores via módulo 11).
 *
 * Espelha 1:1 o helper do backend (src/services/validateCpfCnpj.js no servidor),
 * para que a validação client-side antecipe a mesma rejeição feita no boundary
 * da API — sem chamada de rede.
 *
 * Uso típico (boundary de UI, no blur do campo):
 *   const docCheck = validateCpfCnpj(form.customer_doc);
 *   if (!docCheck.valid) { setError(docCheck.reason); ... }
 */

export function sanitizeDoc(input) {
   if (input === null || input === undefined) return '';
   return String(input).replace(/\D/g, '');
}

function isAllSameDigit(digits) {
   return /^(\d)\1+$/.test(digits);
}

export function isValidCpf(digits) {
   if (typeof digits !== 'string' || digits.length !== 11) return false;
   if (isAllSameDigit(digits)) return false;

   let sum = 0;
   for (let i = 0; i < 9; i += 1) sum += Number(digits[i]) * (10 - i);
   let d1 = (sum * 10) % 11;
   if (d1 === 10) d1 = 0;
   if (d1 !== Number(digits[9])) return false;

   sum = 0;
   for (let i = 0; i < 10; i += 1) sum += Number(digits[i]) * (11 - i);
   let d2 = (sum * 10) % 11;
   if (d2 === 10) d2 = 0;
   if (d2 !== Number(digits[10])) return false;

   return true;
}

export function isValidCnpj(digits) {
   if (typeof digits !== 'string' || digits.length !== 14) return false;
   if (isAllSameDigit(digits)) return false;

   const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
   let sum = 0;
   for (let i = 0; i < 12; i += 1) sum += Number(digits[i]) * weights1[i];
   let d1 = sum % 11;
   d1 = d1 < 2 ? 0 : 11 - d1;
   if (d1 !== Number(digits[12])) return false;

   const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
   sum = 0;
   for (let i = 0; i < 13; i += 1) sum += Number(digits[i]) * weights2[i];
   let d2 = sum % 11;
   d2 = d2 < 2 ? 0 : 11 - d2;
   if (d2 !== Number(digits[13])) return false;

   return true;
}

/**
 * Auto-detecta CPF (11 dígitos) ou CNPJ (14 dígitos) e valida.
 *
 * @param {string|null|undefined} input
 * @returns {{ valid: boolean, kind: 'CPF'|'CNPJ'|null, sanitized: string, reason: string|null }}
 */
export function validateCpfCnpj(input) {
   const sanitized = sanitizeDoc(input);

   if (!sanitized) {
      return {
         valid: false,
         kind: null,
         sanitized: '',
         reason: 'CPF/CNPJ não informado',
      };
   }

   if (sanitized.length === 11) {
      if (isValidCpf(sanitized)) {
         return { valid: true, kind: 'CPF', sanitized, reason: null };
      }
      return {
         valid: false,
         kind: 'CPF',
         sanitized,
         reason: 'CPF com dígitos verificadores inválidos',
      };
   }

   if (sanitized.length === 14) {
      if (isValidCnpj(sanitized)) {
         return { valid: true, kind: 'CNPJ', sanitized, reason: null };
      }
      return {
         valid: false,
         kind: 'CNPJ',
         sanitized,
         reason: 'CNPJ com dígitos verificadores inválidos',
      };
   }

   return {
      valid: false,
      kind: null,
      sanitized,
      reason: `CPF/CNPJ com quantidade de dígitos inválida (recebido: ${sanitized.length}, esperado: 11 para CPF ou 14 para CNPJ)`,
   };
}

export default validateCpfCnpj;
